import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import api from '@/lib/api';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. The link may have expired.');
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-white" data-testid="reset-password-page">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-slate-900 mb-2">Invalid Link</h3>
          <p className="text-sm text-slate-500 mb-6">This password reset link is invalid.</p>
          <Link to="/forgot-password" className="text-sm text-slate-700 font-medium hover:underline">Request a new reset link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-white" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="relative inline-block mb-8">
          <img src="/logo_transparent.png" alt="RentalHub" className="object-contain mx-auto block" style={{ width: '90vw', maxWidth: '700px', height: 'auto' }} />
        </div>

        {success ? (
          <div className="text-center">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold font-heading text-slate-900 mb-2">Password Reset!</h3>
            <p className="text-sm text-slate-500 mb-6">Your password has been reset successfully.</p>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => navigate('/login')} data-testid="reset-login-btn">
              Sign In
            </Button>
          </div>
        ) : (
          <>
            <h3 className="text-2xl font-semibold font-heading text-slate-900 mb-1">Set new password</h3>
            <p className="text-sm text-slate-500 mb-6">Enter your new password below</p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4 text-sm" data-testid="reset-error">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">New Password</Label>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" className="mt-1.5" required data-testid="reset-password-input" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Confirm Password</Label>
                <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Confirm password" className="mt-1.5" required data-testid="reset-confirm-input" />
              </div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white" disabled={loading} data-testid="reset-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Reset Password
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
